import type { StoreApp } from '@/api/endpointsStores';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Segmented } from '@/features/access/bits';
import { RELEASE_STATUS_OPTIONS, submitSummary, type ReleaseStatus, type SubmitForm } from './bindingSubmitModel';
import type { ProjectEnvironment } from './profileFormModel';
import type { ProfileFormApi } from './useProfileForm';

const NONE = 'none';

/** Track, release status, rollout share and "after each build" of one environment (P29, SPEC §39.4). */
function SubmitFields({ env, form, submit }: { env: ProjectEnvironment; form: ProfileFormApi; submit: SubmitForm }) {
  const set = (change: Partial<SubmitForm>) => form.setBindingSubmit(env.id, change);
  const id = `profile-binding-${env.slug}-submit`;
  return (
    <div data-testid={id} className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)]">
        <label className="flex min-w-0 flex-col gap-1.5 text-[13px] font-medium">
          Track
          <Input data-testid={`${id}-track`} value={submit.track} onChange={(e) => set({ track: e.target.value })} placeholder="internal" className="h-8 font-mono max-sm:h-10 sm:text-[13px]" spellCheck={false} maxLength={64} />
        </label>
        <Segmented<ReleaseStatus> label="Release" testId={`${id}-status`} value={submit.releaseStatus} onChange={(releaseStatus) => set({ releaseStatus })} options={RELEASE_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label, title: o.description }))} />
      </div>
      {submit.releaseStatus === 'inProgress' ? (
        <label className="flex items-center gap-2 text-[13px]">
          <span>First release to</span>
          <Input data-testid={`${id}-rollout`} inputMode="numeric" value={submit.rolloutPercent} onChange={(e) => set({ rolloutPercent: e.target.value.replace(/[^0-9]/g, '') })} className="h-8 w-16 text-right font-mono max-sm:h-10 sm:text-[13px]" aria-label={`Staged rollout percent for ${env.name}`} />
          <span>% of the track&apos;s users</span>
        </label>
      ) : null}
      <label className="flex min-h-11 items-center justify-between gap-3 text-[13px] sm:min-h-0">
        <span>Submit after each build</span>
        <Switch data-testid={`${id}-auto`} checked={submit.autoSubmit} onCheckedChange={(autoSubmit) => set({ autoSubmit })} aria-label={`Submit every ${env.name} build`} />
      </label>
      <p data-testid={`${id}-summary`} className="text-xs text-subtle">{submitSummary(submit)}</p>
    </div>
  );
}

/**
 * Where one Android environment's builds go in Google Play: the store app, and whether and how they are submitted. Shown
 * only when the app has a store app (operators add them in the app's Stores tab).
 */
export function BindingSubmit({ env, form, storeApps }: { env: ProjectEnvironment; form: ProfileFormApi; storeApps: StoreApp[] }) {
  const b = form.values.bindings[env.id];
  const androidApps = storeApps.filter((s) => s.platform === 'android');
  if (!b?.on || form.values.platform !== 'android' || androidApps.length === 0) return null;
  const storeAppId = b.storeAppId ?? '';
  return (
    <div data-testid={`profile-binding-${env.slug}-store`} className="ml-7 flex flex-col gap-3 rounded-lg border border-dashed px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Select value={storeAppId || NONE} onValueChange={(v) => v && form.setBindingStoreApp(env.id, v === NONE ? '' : v)}>
          <SelectTrigger data-testid={`profile-binding-${env.slug}-store-app`} className="h-8 min-w-0 max-w-full text-[13px]" aria-label={`Google Play app for ${env.name}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>No store submission</SelectItem>
            {androidApps.map((s) => <SelectItem key={s.id} value={s.id}>Google Play · {s.identifier}</SelectItem>)}
          </SelectContent>
        </Select>
        {storeAppId ? (
          <label className="flex min-h-11 items-center gap-2 text-[13px] sm:min-h-0">
            <span>Submit builds</span>
            <Switch data-testid={`profile-binding-${env.slug}-submit-on`} checked={Boolean(b.submit)} onCheckedChange={(on) => form.setBindingSubmit(env.id, on ? {} : null)} aria-label={`Submit ${env.name} builds to Google Play`} />
          </label>
        ) : null}
      </div>
      {storeAppId && b.submit ? <SubmitFields env={env} form={form} submit={b.submit} /> : null}
    </div>
  );
}
